// DevHub UI kit — unified entity card.
//
// Merges DocCard (docs grids) and CatalogPage's CatalogEntityCard (catalog grids):
// both rendered the same visual — icon + title + star toggle, description, author
// byline, and an owner + kind/type/lifecycle badge footer. Bundled here so every
// docs/catalog/API grid renders one component.
//
// Data is read through the shared entityHelpers (icon, path, owner, lifecycle,
// type, author); the star toggle is the shared StarToggle. Styling uses the
// existing docs-card classes (co-located in EntityCard.css).
import { useState } from 'react';
import { Entity } from '@backstage/catalog-model';
import { Link } from 'react-router-dom';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useTechDocsCookie } from '@internal/plugin-isdp-common/ui';
import {
  getOwner,
  ownerLabel,
  getLifecycle,
  getType,
  getAuthor,
  entityPath,
  docsIcon,
  isTechdocsDocsIcon,
} from '../catalog/entityHelpers';
import { StarToggle } from './StarToggle';
import './EntityCard.css';

export interface EntityCardProps {
  entity: Entity;
  /** Destination for the card link. Defaults to the entity detail page. */
  to?: string;
  /** Explicit icon URL/glyph override. Otherwise resolved from annotations. */
  icon?: string;
  /** Show the favorite toggle (default true). */
  showStar?: boolean;
}

// Card icon slot: an explicit override, then the self-serve logo annotations
// (isdp.io/icon, isdp.io/landscape-logo, draeger.com/docs-icon), else the ODX
// document glyph. Falls back to the glyph if an image URL fails to load.
// A docs-icon that is an image in the doc's own repo (`assets/logo.png`) is served
// by TechDocs and needs its user cookie first; until that is in place the glyph shows,
// and if it can't be had (or the doc hasn't been built yet) the glyph stays.
const CardIcon = ({ entity, icon }: { entity: Entity; icon?: string }) => {
  const explicit =
    icon ??
    entity.metadata.annotations?.['isdp.io/icon'] ??
    entity.metadata.annotations?.['isdp.io/landscape-logo'];
  const techdocsAsset = !explicit && isTechdocsDocsIcon(entity);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const cookie = useTechDocsCookie({ discoveryApi, fetchApi }, techdocsAsset);
  let url: string | undefined = explicit;
  if (!url && !techdocsAsset) url = docsIcon(entity) ?? undefined;
  if (!url && techdocsAsset && cookie.status === 'ready') {
    url = docsIcon(entity, cookie.techdocsBase) ?? undefined;
  }
  const [failed, setFailed] = useState(false);
  if (url && !failed) {
    return (
      <img
        className="devhub-docs-icon-img"
        src={url}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
      />
    );
  }
  return <odx-icon name="core::document" size="md" />;
};

export const EntityCard = ({
  entity,
  to,
  icon,
  showStar = true,
}: EntityCardProps) => {
  const title = entity.metadata.title || entity.metadata.name;
  const description = entity.metadata.description || '';
  const owner = getOwner(entity);
  const type = getType(entity);
  const lifecycle = getLifecycle(entity);
  const author = getAuthor(entity);

  return (
    <Link
      to={to ?? entityPath(entity)}
      className="devhub-docs-card-link devhub-catalog-entity-card"
    >
      <odx-card interactive>
        <div className="devhub-docs-card-inner">
          <div className="devhub-docs-card-inner__header">
            <CardIcon entity={entity} icon={icon} />
            <span className="devhub-docs-card-inner__title">{title}</span>
            {showStar && <StarToggle entity={entity} />}
          </div>
          {description && (
            <p className="devhub-docs-card-inner__desc">{description}</p>
          )}
          {author && (
            <div
              className="devhub-docs-card-inner__author"
              title={author.email || undefined}
            >
              <odx-icon name="core::user" size="sm" />
              <span>{author.name}</span>
            </div>
          )}
          <div className="devhub-docs-card-inner__footer">
            {owner && (
              <span className="devhub-docs-card-inner__owner">
                {ownerLabel(owner)}
              </span>
            )}
            {type && <span className="devhub-docs-kind-badge">{type}</span>}
            {lifecycle && (
              <span className="devhub-docs-kind-badge">{lifecycle}</span>
            )}
            <span className="devhub-docs-kind-badge">{entity.kind}</span>
          </div>
        </div>
      </odx-card>
    </Link>
  );
};
